import { ChangeDetectionStrategy, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { FlexAlignDirective } from '@app/core/shared/directives/alignment/flex-align.directive';
import { FlexBetweenDirective } from '@app/core/shared/directives/alignment/flex-between.directive';
import { FlexDirectionDirective } from '@app/core/shared/directives/alignment/flex-direction.directive';
import { TextAlignDirective } from '@app/core/shared/directives/alignment/text-align.directive';
import { FlexGapDirective } from '@app/core/shared/directives/spacing/flex-gap.directive';
import { TextColourDirective } from '@app/core/shared/directives/text-colour.directive';
import { BodyLargeDirective } from '@app/core/shared/directives/typography/body-large.directive';
import { TitleLargeDirective } from '@app/core/shared/directives/typography/title-large.directive';
import { NavigateToService } from '@app/core/shared/services/helper-services/navigate-to.service';
import { NotificationService } from '@app/core/shared/services/notification-service';
import { NotificationsHttpService } from '@app/core/shared/services/notifications-http.service';
import { MaterialSymbol } from 'material-symbols';
import { NotificationDetailsComponent } from '../notification-details/notification-details.component';
import { NotificationsStateService } from '../notifications-state.service';

@Component({
  selector: 'app-notifications-dialog',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatIcon,
    MatButton,
    MatIconButton,
    MatCard,
    NotificationDetailsComponent,
    TitleLargeDirective,
    FlexBetweenDirective,
    FlexAlignDirective,
    FlexGapDirective,
    FlexDirectionDirective,
    TextColourDirective,
    BodyLargeDirective,
    TextAlignDirective
  ],
  templateUrl: './notifications-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsDialogComponent {
  @ViewChild('anchor') set anchorRef(element: ElementRef) {
    if (element) {
      this.observer.observe(element.nativeElement);
    }
  }

  private readonly dialogRef = inject(MatDialogRef<NotificationsDialogComponent>);
  private readonly notificationStateService = inject(NotificationsStateService);
  private readonly notificationService = inject(NotificationsHttpService);
  private readonly toastService = inject(NotificationService);
  private readonly navigateTo = inject(NavigateToService);

  private observer: IntersectionObserver;

  protected readonly openInNewIcon: MaterialSymbol = 'open_in_new';
  protected readonly flag2Icon: MaterialSymbol = 'flag_2';

  $notifications = this.notificationStateService.unreadNotifications;

  ngOnInit() {
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.loadMore();
      }
    });
  }

  ngOnDestroy() {
    this.observer.disconnect();
  }

  loadMore() {
    this.notificationStateService.loadMoreUnreads();
  }

  openNotifications(): void {
    this.navigateTo.notifications().then(() => this.dialogRef.close());
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        const ids = this.$notifications().map(item => item.id);
        this.notificationStateService.getUnreads();
        this.notificationStateService.updateAsRead(ids, true);
        this.notificationStateService.refreshStatistics();
        this.dialogRef.close();
      },
      error: err => {
        this.toastService.notifyFailure('Failed to mark notifications as read.');
        console.error(err);
      }
    });
  }
}
